import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api';
import { useToast } from '../components/Toast';
import { computeCompleteness } from '../hooks/useProfileCompleteness';
import UpgradeBanner from '../components/UpgradeBanner';
import { useProfiles, useUsage, QK } from '../queries';

const s = {
  page: { maxWidth: 720, margin: '32px auto', padding: '0 24px 60px' },
  heading: { fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  subheading: { fontSize: 15, color: '#64748b', marginBottom: 28 },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  newBtn: {
    background: '#1e40af', color: '#fff', border: 'none',
    fontWeight: 600, fontSize: 14, padding: '9px 18px', borderRadius: 8, cursor: 'pointer',
  },
  newBtnDisabled: {
    background: '#93c5fd', color: '#fff', border: 'none',
    fontWeight: 600, fontSize: 14, padding: '9px 18px', borderRadius: 8, cursor: 'not-allowed',
  },
  card: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: '20px 24px', marginBottom: 14,
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  profileName: { fontSize: 16, fontWeight: 600, color: '#0f172a' },
  companyName: { fontSize: 14, color: '#64748b', marginBottom: 14 },
  progressBarWrap: { background: '#e2e8f0', borderRadius: 8, height: 6, marginBottom: 6, maxWidth: 260 },
  progressBar: (pct) => ({
    background: pct >= 80 ? '#16a34a' : pct >= 50 ? '#f59e0b' : '#dc2626',
    width: pct + '%', height: '100%', borderRadius: 8, transition: 'width 0.3s',
  }),
  completenessLabel: { fontSize: 12, color: '#94a3b8', marginBottom: 14 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  editBtn: {
    background: '#1e40af', color: '#fff', border: 'none',
    fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
  },
  deleteBtn: {
    background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5',
    fontSize: 13, fontWeight: 500, padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
  },
  loadingPage: { textAlign: 'center', padding: 80, color: '#64748b' },
  emptyBox: {
    textAlign: 'center', padding: '60px 20px',
    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
  },
  emptyTitle: { fontSize: 18, fontWeight: 600, color: '#1e293b', marginBottom: 10 },
  emptyText: { fontSize: 14, color: '#64748b', marginBottom: 20 },
};

export default function ProfileList() {
  const navigate = useNavigate();
  const showToast = useToast();
  const queryClient = useQueryClient();
  const profilesQuery = useProfiles();
  const usageQuery = useUsage();
  const profiles = profilesQuery.data ?? [];
  const usage = usageQuery.data ?? null;
  const loading = profilesQuery.isLoading;

  async function handleDelete(profile) {
    if (!window.confirm(`Delete profile "${profile.profile_name}"? This will remove all associated matches.`)) return;
    try {
      await apiFetch(`/profiles/${profile.id}`, { method: 'DELETE' });
      showToast('Profile deleted', 'success');
      queryClient.invalidateQueries({ queryKey: QK.profiles });
      queryClient.invalidateQueries({ queryKey: QK.usage });
      queryClient.invalidateQueries({ queryKey: QK.matches });
    } catch (err) {
      showToast('Failed to delete profile: ' + err.message, 'error');
    }
  }

  function handleNewProfile() {
    navigate('/onboarding?source=profile');
  }

  const atLimit = usage && usage.profiles_max > 0 && profiles.length >= usage.profiles_max;

  if (loading) return <div style={s.loadingPage}>Loading profiles…</div>;

  return (
    <div style={s.page}>
      <div style={s.headerRow}>
        <div>
          <h1 style={s.heading}>My Profiles</h1>
          <p style={s.subheading}>Each profile generates its own set of tender matches.</p>
        </div>
        <button
          style={atLimit ? s.newBtnDisabled : s.newBtn}
          disabled={atLimit}
          onClick={handleNewProfile}
        >
          + New profile
        </button>
      </div>

      {atLimit && (
        <UpgradeBanner
          title="Profile limit reached"
          message={`Your plan allows ${usage.profiles_max} active profile${usage.profiles_max !== 1 ? 's' : ''}. Upgrade to add more.`}
          cta="Upgrade plan"
        />
      )}

      {profiles.length === 0 ? (
        <div style={s.emptyBox}>
          <div style={s.emptyTitle}>No profiles yet</div>
          <div style={s.emptyText}>Create a profile to start finding matching tenders.</div>
          <button style={s.newBtn} onClick={handleNewProfile}>Create first profile</button>
        </div>
      ) : (
        profiles.map((profile) => {
          const { score, missing } = computeCompleteness(profile);
          return (
            <div key={profile.id} style={s.card}>
              <div style={s.cardHeader}>
                <span style={s.profileName}>{profile.profile_name}</span>
              </div>
              <div style={s.companyName}>{profile.company_name}{profile.sector ? ` — ${profile.sector}` : ''}</div>
              <div style={s.progressBarWrap}>
                <div style={s.progressBar(score)} />
              </div>
              <div style={s.completenessLabel}>
                {score}% complete{missing.length > 0 ? ` — missing: ${missing.map((m) => m.label).join(', ')}` : ''}
              </div>
              <div style={s.actions}>
                <button style={s.editBtn} onClick={() => navigate(`/profiles/${profile.id}/edit`)}>Edit</button>
                <button style={s.deleteBtn} onClick={() => handleDelete(profile)}>Delete</button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
